import React, { useEffect, useMemo, useState } from "react";
import { toBRDate } from "../../../core/cimInterdicoes.utils";
import InterdicaoDetalhesLocomotivasFields from "./tipos/InterdicaoDetalhesLocomotivasFields";
import InterdicaoDetalhesToFields from "./tipos/InterdicaoDetalhesToFields";
import InterdicaoDetalhesVagoesFields from "./tipos/InterdicaoDetalhesVagoesFields";
import InterdicaoDetalhesViaFields from "./tipos/InterdicaoDetalhesViaFields";
import "./InterdicaoDetalhesModal.css";

const MIN_STATUS_FIELDS = 5;
const MAX_STATUS_FIELDS = 10;

function normalizarHistorico(values) {
  const base = Array.isArray(values)
    ?values.map((value) => String(value || ""))
    : values
      ?[String(values)]
      : [];
  const limitado = base.slice(0, MAX_STATUS_FIELDS);

  let ultimoPreenchido = -1;
  for (let index = limitado.length - 1; index >= 0; index -= 1) {
    if (limitado[index]?.trim()) {
      ultimoPreenchido = index;
      break;
    }
  }

  const totalDesejado = Math.min(
    MAX_STATUS_FIELDS,
    Math.max(MIN_STATUS_FIELDS, ultimoPreenchido + 2)
  );
  const next = limitado.slice(0, totalDesejado);

  while (next.length < totalDesejado) next.push("");

  return next;
}

function salvarHistorico(values) {
  return values
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .slice(0, MAX_STATUS_FIELDS);
}

function ReadOnlyField({ label, value, wide = false, multiline = false }) {
  return (
    <label className={`interdDet-field${wide ?" interdDet-full" : ""}`}>
      <span className="interdDet-label">{label}</span>
      {multiline ?(
        <textarea
          className="interdDet-textarea interdDet-inputRO"
          value={value || ""}
          readOnly
          disabled
        />
      ) : (
        <input
          className="interdDet-input interdDet-inputRO"
          value={value || ""}
          readOnly
          disabled
        />
      )}
    </label>
  );
}

function Section({ title, children }) {
  return (
    <section className="interdDet-blockRO">
      <div className="interdDet-sectionTitleRow">
        <h4 className="interdDet-sectionTitle">{title}</h4>
      </div>
      {children}
    </section>
  );
}

function formatDate(value) {
  return value ?toBRDate(value) : "-";
}

function resolveDetailValue(interdicao, ...keys) {
  const detalhes =
    interdicao?.detalhesTipo && typeof interdicao.detalhesTipo === "object"
      ?interdicao.detalhesTipo
      : {};

  for (const key of keys) {
    const directValue = interdicao?.[key];
    if (directValue !== undefined && directValue !== null && String(directValue).trim() !== "") {
      return String(directValue);
    }

    const detailValue = detalhes?.[key];
    if (detailValue !== undefined && detailValue !== null && String(detailValue).trim() !== "") {
      return String(detailValue);
    }
  }

  return "";
}

function getPrevisaoConfig(tipoInterdicao) {
  if (String(tipoInterdicao || "").trim().toUpperCase() === "VIA") {
    return {
      title: "Previsão de Encerramento",
      dateLabel: "Data de encerramento",
      timeLabel: "Hora de encerramento",
    };
  }

  return {
    title: "Previsão de Liberação",
    dateLabel: "Data de liberação",
    timeLabel: "Horário de liberação",
  };
}

function renderTipoFields(tipoInterdicao, props) {
  const normalizedTipo = String(tipoInterdicao || "").trim().toUpperCase();

  switch (normalizedTipo) {
    case "TO":
      return <InterdicaoDetalhesToFields {...props} />;
    case "LOCOMOTIVAS":
      return <InterdicaoDetalhesLocomotivasFields {...props} />;
    case "VAGÕES":
      return <InterdicaoDetalhesVagoesFields {...props} />;
    case "VAGOES":
      return <InterdicaoDetalhesVagoesFields {...props} />;
    case "VIA":
    default:
      return <InterdicaoDetalhesViaFields {...props} />;
  }
}

export default function InterdicaoDetalhesModal({
  open,
  interdicao,
  onClose,
  onSave,
  onFinalize,
  saving = false,
  savingAction = "",
  saveError = "",
}) {
  const [form, setForm] = useState({
    temPrevisao: "nao",
    dataFim: "",
    horaFim: "",
    historicoStatus: [""],
  });

  useEffect(() => {
    if (!open || !interdicao) return;

    setForm({
      temPrevisao: interdicao.temPrevisao ?"sim" : "nao",
      dataFim: interdicao.dataFim || "",
      horaFim: interdicao.horaFim || "",
      historicoStatus: normalizarHistorico(interdicao.historicoStatus),
    });
  }, [interdicao, open]);

  const historicoSalvo = useMemo(
    () => salvarHistorico(form.historicoStatus),
    [form.historicoStatus]
  );
  const isViaAwaitingCco =
    String(interdicao?.tipoInterdicao || "").trim().toUpperCase() === "VIA" &&
    String(interdicao?.status || "").trim() === "Aguardando CCO";
  const previsaoConfig = useMemo(
    () => getPrevisaoConfig(interdicao?.tipoInterdicao),
    [interdicao?.tipoInterdicao]
  );
  const canSave = useMemo(() => {
    if (isViaAwaitingCco) return false;
    if (!historicoSalvo.length) return false;
    if (form.temPrevisao === "sim") return Boolean(form.dataFim && form.horaFim);
    return true;
  }, [form.dataFim, form.horaFim, form.temPrevisao, historicoSalvo.length, isViaAwaitingCco]);
  const canFinalize = canSave && String(interdicao?.status || "").trim() !== "Finalizado";

  if (!open || !interdicao) return null;

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((current) => {
      if (field === "temPrevisao") {
        return {
          ...current,
          temPrevisao: value,
          dataFim: value === "sim" ?current.dataFim : "",
          horaFim: value === "sim" ?current.horaFim : "",
        };
      }

      return { ...current, [field]: value };
    });
  };

  const handleStatusChange = (index) => (event) => {
    const value = event.target.value;

    setForm((current) => {
      const next = current.historicoStatus.slice();
      next[index] = value;

      return {
        ...current,
        historicoStatus: normalizarHistorico(next),
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSave || saving) return;

    const temPrevisao = form.temPrevisao === "sim";

    Promise.resolve(
      onSave?.(interdicao.id, {
        temPrevisao,
        dataFim: temPrevisao ?form.dataFim : "",
        horaFim: temPrevisao ?form.horaFim : "",
        historicoStatus: historicoSalvo,
      })
    )
      .then(() => {
        onClose?.();
      })
      .catch(() => {});
  };

  const handleFinalize = () => {
    if (!canFinalize || saving) return;

    const temPrevisao = form.temPrevisao === "sim";

    Promise.resolve(
      onFinalize?.(interdicao.id, {
        temPrevisao,
        dataFim: temPrevisao ?form.dataFim : "",
        horaFim: temPrevisao ?form.horaFim : "",
        historicoStatus: historicoSalvo,
      })
    )
      .then(() => {
        onClose?.();
      })
      .catch(() => {});
  };

  const tipoFields = renderTipoFields(interdicao.tipoInterdicao, {
    Section,
    ReadOnlyField,
    formatDate,
    interdicao,
    resolveValue: (...keys) => resolveDetailValue(interdicao, ...keys),
  });

  return (
    <div className="interdDet-backdrop" role="presentation">
      <div
        className="interdDet-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="interdDet-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="interdDet-header">
          <div className="interdDet-title" id="interdDet-title">
            Ficha da Interdição
          </div>

          <button
            className="interdDet-close"
            type="button"
            onClick={onClose}
            aria-label="Fechar ficha"
          >
            ×
          </button>
        </div>

        <form className="interdDet-shell" onSubmit={handleSubmit}>
          <div className="interdDet-content">
            {tipoFields}

            <section className="interdDet-blockEdit">
              <div className="interdDet-sectionTitleRow">
                <h4 className="interdDet-sectionTitle">{previsaoConfig.title}</h4>
              </div>

              <div className="interdDet-grid interdDet-grid--3">
                <label className="interdDet-field">
                  <span className="interdDet-label">Tem previsão?</span>
                  <select
                    className="interdDet-input"
                    value={form.temPrevisao}
                    onChange={handleChange("temPrevisao")}
                    disabled={isViaAwaitingCco}
                  >
                    <option value="nao">Não</option>
                    <option value="sim">Sim</option>
                  </select>
                </label>

                <label
                  className={`interdDet-field${form.temPrevisao === "sim" ?"" : " is-disabled"}`}
                >
                  <span className="interdDet-label">{previsaoConfig.dateLabel}</span>
                  <input
                    className="interdDet-input"
                    type="date"
                    value={form.dataFim}
                    onChange={handleChange("dataFim")}
                    disabled={isViaAwaitingCco || form.temPrevisao !== "sim"}
                  />
                </label>

                <label
                  className={`interdDet-field${form.temPrevisao === "sim" ?"" : " is-disabled"}`}
                >
                  <span className="interdDet-label">{previsaoConfig.timeLabel}</span>
                  <input
                    className="interdDet-input"
                    type="time"
                    value={form.horaFim}
                    onChange={handleChange("horaFim")}
                    disabled={isViaAwaitingCco || form.temPrevisao !== "sim"}
                  />
                </label>
              </div>
            </section>

            <section className="interdDet-blockEdit">
              <div className="interdDet-sectionTitleRow">
                <h4 className="interdDet-sectionTitle">Status da Interdição</h4>
              </div>

              <div className="interdDet-statusStack">
                {form.historicoStatus.map((status, index) => (
                  <label className="interdDet-field" key={`status-${index}`}>
                    <span className="interdDet-label">{`Acompanhamento ${index + 1}`}</span>
                    <textarea
                      className="interdDet-textarea interdDet-statusTextarea"
                      value={status}
                      onChange={handleStatusChange(index)}
                      disabled={isViaAwaitingCco}
                      placeholder="Descreva o que está acontecendo, andamento da equipe, bloqueio, liberação prevista ou qualquer atualização relevante."
                    />
                  </label>
                ))}
              </div>
            </section>
          </div>

          <div className="interdDet-actions">
            <div className="interdDet-actionsRight">
              {isViaAwaitingCco ?(
                <div className="interdDet-note">
                  VIA aguardando aprovação do CCO. A ficha libera edição depois de Aprovado CCO.
                </div>
              ) : null}
              {saveError ?<div className="interdDet-error">{saveError}</div> : null}
              <button
                type="submit"
                className="interdDet-btn interdDet-btnPrimary"
                disabled={!canSave || saving}
              >
                {saving && savingAction === "salvar" ?"Salvando..." : "Salvar"}
              </button>
              <button
                type="button"
                className="interdDet-btn interdDet-btnFinalizar"
                disabled={!canFinalize || saving}
                onClick={handleFinalize}
              >
                {saving && savingAction === "finalizar" ?"Finalizando..." : "Finalizar"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
